// Settings — clinician-only. Houses the Order-a-Test autofill toggle and
// signature management (replace signature, versioned/append-only on the
// backend — the old one is kept, never overwritten). New page rather than
// repurposing /change-password: this is a genuine small cluster of
// clinician preferences, not a password-specific flow.
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Switch, Card, Button, Alert, CircularProgress, Checkbox, FormControlLabel } from "@mui/material";
import { CheckCircle, ToggleOn, Draw } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import UserContext from "./UserContext";
import SignaturePad from "./SignaturePad";
import ClinicianPageHeader from "./ClinicianPageHeader";
import { extractErrorDetail } from "./validators";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#00357a", navyDark: "#001f4d", gold: "#c9a96e" };

function SettingIcon({ children, isDark }) {
  return (
    <Box sx={{
      width: 40, height: 40, borderRadius: "11px", flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      bgcolor: isDark ? "rgba(201,169,110,0.12)" : "rgba(0,53,122,0.07)",
      color: isDark ? BRAND.gold : BRAND.navy,
    }}>
      {children}
    </Box>
  );
}

export default function Settings() {
  // 2026-10-13 fix — this page hardcoded light-mode colors throughout
  // despite rendering a working ThemeToggle (in ClinicianPageHeader) —
  // confirmed real gap, useTheme()/isDark were entirely absent here.
  // Same useTheme() + palette.mode pattern Login.js/Register.js already
  // use correctly, not a new convention.
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { user } = useContext(UserContext);
  const token = user?.token;
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [loading, setLoading] = useState(true);
  const [autofillEnabled, setAutofillEnabled] = useState(true);
  const [hasSignature, setHasSignature] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [consentText, setConsentText] = useState("");
  const [replacingSignature, setReplacingSignature] = useState(false);
  const [newSignatureConsent, setNewSignatureConsent] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);
  const signatureRef = useRef(null);

  useEffect(() => {
    axios.get(`${API_BASE}/clinician/settings`, { headers: authHeaders })
      .then((res) => {
        setAutofillEnabled(res.data.order_form_autofill_enabled !== false);
        setHasSignature(!!res.data.has_signature);
      })
      .catch(() => setError("Could not load your settings."))
      .finally(() => setLoading(false));
    axios.get(`${API_BASE}/api/clinician-signature-consent-text`)
      .then((res) => setConsentText(res.data.consent_text || ""))
      .catch(() => {});
  }, [authHeaders]);

  const toggleAutofill = async (checked) => {
    setAutofillEnabled(checked);
    setSavingToggle(true);
    try {
      await axios.put(`${API_BASE}/clinician/settings`, { order_form_autofill_enabled: checked }, { headers: authHeaders });
    } catch {
      setAutofillEnabled(!checked); // revert on failure
      setError("Could not save this preference — please try again.");
    } finally {
      setSavingToggle(false);
    }
  };

  const saveSignature = async () => {
    setError(""); setSuccess("");
    const pngBase64 = signatureRef.current?.getPngBase64();
    if (!pngBase64) return setError("Please draw your new signature first.");
    if (!newSignatureConsent) return setError("Please consent to using this signature on test order requisitions.");
    setSavingSignature(true);
    try {
      await axios.put(`${API_BASE}/clinician/settings/signature`, {
        signature_png_base64: pngBase64, signature_consent: newSignatureConsent,
      }, { headers: authHeaders });
      setHasSignature(true);
      setReplacingSignature(false);
      setNewSignatureConsent(false);
      setSuccess("Your signature has been updated. Past requisitions keep the signature that was in effect when they were signed.");
    } catch (err) {
      setError(extractErrorDetail(err, "Could not save your new signature."));
    } finally {
      setSavingSignature(false);
    }
  };

  const cardBorder = isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.1)";
  const cardBg = isDark ? "#111827" : "#fff";
  const headingColor = isDark ? "#f1f5f9" : "#1e293b";
  const bodyColor = isDark ? "#94a3b8" : "#64748b";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: isDark ? "#080d1a" : "#f8f6f2" }}>
      <ClinicianPageHeader
        title="Settings"
        subtitle="Manage your Order a Test preferences and your signature on file."
      />

      <Box sx={{ maxWidth: 640, mx: "auto", px: 3, py: 5 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: BRAND.navy }} /></Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError("")}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

            <Card sx={{
              borderRadius: 4, p: 3.5, mb: 3, border: cardBorder,
              bgcolor: cardBg, boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,53,122,0.06)",
            }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
                <Box sx={{ display: "flex", gap: 1.75 }}>
                  <SettingIcon isDark={isDark}><ToggleOn sx={{ fontSize: 22 }} /></SettingIcon>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 15.5, color: headingColor, mb: 0.5 }}>Autofill patient details</Typography>
                    <Typography sx={{ fontSize: 13.5, color: bodyColor, maxWidth: 380, lineHeight: 1.6 }}>
                      When on, selecting a returning patient on the Order a Test page fills in their saved demographics
                      and billing details automatically. When off, you'll start from a blank form every time.
                    </Typography>
                  </Box>
                </Box>
                <Switch checked={autofillEnabled} disabled={savingToggle} onChange={(e) => toggleAutofill(e.target.checked)}
                  sx={{
                    flexShrink: 0,
                    // 2026-10-13 fix — real contrast gap caught by review:
                    // the checked THUMB and its TRACK were both plain
                    // BRAND.navy — on a dark card the navy track already
                    // reads as "unlit," so a same-color navy thumb on top
                    // of it was functionally invisible (this was arguably
                    // already low-contrast in light mode too, just less
                    // noticeable against a white card). Gold thumb in dark
                    // mode now reads as a clearly distinct "on" state.
                    "& .MuiSwitch-switchBase.Mui-checked": { color: isDark ? BRAND.gold : BRAND.navy },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: BRAND.navy, opacity: 1 },
                  }} />
              </Box>
            </Card>

            <Card sx={{
              borderRadius: 4, p: 3.5, border: cardBorder,
              bgcolor: cardBg, boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,53,122,0.06)",
            }}>
              <Box sx={{ display: "flex", gap: 1.75, alignItems: "flex-start", mb: 2 }}>
                <SettingIcon isDark={isDark}><Draw sx={{ fontSize: 20 }} /></SettingIcon>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 15.5, color: headingColor }}>Your Signature</Typography>
                    {hasSignature && !replacingSignature && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <CheckCircle sx={{ fontSize: 16, color: "#22c55e" }} />
                        <Typography sx={{ fontSize: 13, color: "#22c55e", fontWeight: 700 }}>On file</Typography>
                      </Box>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 13.5, color: bodyColor, maxWidth: 420, lineHeight: 1.6, mt: 0.5 }}>
                    Used to sign every test order you submit — no need to re-draw it per order.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ height: "1px", bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.08)", mb: 2.25 }} />

              {!replacingSignature ? (
                <Button variant="outlined" onClick={() => setReplacingSignature(true)}
                  sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", borderColor: BRAND.navy, color: isDark ? BRAND.gold : BRAND.navy,
                    "&:hover": { borderColor: BRAND.navy, bgcolor: isDark ? "rgba(201,169,110,0.08)" : "rgba(0,53,122,0.05)" } }}>
                  {hasSignature ? "Replace Signature" : "Add Signature"}
                </Button>
              ) : (
                <Box>
                  <Typography sx={{ fontSize: 13.5, color: bodyColor, mb: 1.5 }}>
                    Your new signature will be used on every order you sign going forward. Past requisitions will
                    continue to show the signature that was in effect when they were signed.
                  </Typography>
                  <SignaturePad ref={signatureRef} isDark={isDark} />
                  <FormControlLabel
                    sx={{ alignItems: "flex-start", mt: 1 }}
                    control={<Checkbox checked={newSignatureConsent} onChange={(e) => setNewSignatureConsent(e.target.checked)}
                      sx={{ color: isDark ? "rgba(255,255,255,0.4)" : BRAND.navy, mt: -0.5, "&.Mui-checked": { color: isDark ? BRAND.gold : BRAND.navy } }} />}
                    label={<Typography sx={{ fontSize: 13, color: bodyColor, lineHeight: 1.6 }}>
                      {consentText || "By drawing and submitting your signature, you consent to Labarium using it to sign test order requisitions you submit through your account."}
                    </Typography>}
                  />
                  <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
                    <Button onClick={() => { setReplacingSignature(false); setNewSignatureConsent(false); }} sx={{ textTransform: "none", color: bodyColor, fontWeight: 600 }}>
                      Cancel
                    </Button>
                    <Button variant="contained" disabled={savingSignature} onClick={saveSignature}
                      sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: BRAND.navy, "&:hover": { bgcolor: BRAND.navyDark } }}>
                      {savingSignature ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save Signature"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Card>
          </>
        )}
      </Box>
    </Box>
  );
}
